import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import LotteryWeekSelector, {
  getInitialWeekStart,
  getWeekRange,
} from '../../../components/LotteryWeekSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import apiClient from '../../../utils/axiosClient';
import type { ShopNpayExchangeItem } from '../../../types/luckyLottery.types';

dayjs.locale('ko');

const STORAGE_KEY = 'avatye.ops.lucky-lottery.exchange.npay.filter';

type StoredFilter = { startDate: string; endDate: string; appName: string };

const getStoredFilter = (): StoredFilter | null => {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s) return JSON.parse(s) as StoredFilter;
  } catch {
    /* ignore */
  }
  return null;
};

const saveFilter = (f: StoredFilter) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(f));
  } catch {
    /* ignore */
  }
};

const fmt = (n: number) => n.toLocaleString('ko-KR');

function StatCard({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <Box sx={{ backgroundColor: '#f8fafc', borderRadius: 3, p: 2.5, border: '1px solid #f1f5f9' }}>
      <Typography sx={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500, mb: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.55rem', fontWeight: 700, color: '#0044D2', lineHeight: 1.2 }}>
        {value}
      </Typography>
      {unit && (
        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>{unit}</Typography>
      )}
    </Box>
  );
}

export default function LuckyLotteryNpayExchange() {
  const stored = useMemo(() => getStoredFilter(), []);

  const initialWeek = useMemo(() => {
    if (stored?.startDate && stored?.endDate)
      return { startDate: stored.startDate, endDate: stored.endDate };
    const sunday = getInitialWeekStart();
    const { start, end } = getWeekRange(sunday);
    return { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') };
  }, [stored]);

  const [startDate, setStartDate] = useState(initialWeek.startDate);
  const [endDate, setEndDate] = useState(initialWeek.endDate);
  const [appName, setAppName] = useState<string>(stored?.appName ?? '앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [selectedOs, setSelectedOs] = useState<string>('all');
  const [data, setData] = useState<ShopNpayExchangeItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchAppOptions = useCallback(() => {
    if (!startDate || !endDate) return;
    apiClient
      .get<string[]>('/api/v1/stat/lucky-lottery/app/list', {
        params: { start_date: startDate, end_date: endDate },
      })
      .then((res) => setAppOptions(['앱 전체', ...(res.data || [])]))
      .catch(() => undefined);
  }, [startDate, endDate]);

  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;
    const app = appName === '앱 전체' ? null : appName;
    saveFilter({ startDate, endDate, appName });
    setLoading(true);
    apiClient
      .get<ShopNpayExchangeItem[]>('/api/v1/analytics/lucky-lottery/exchange/npay', {
        params: {
          start_date: startDate,
          end_date: endDate,
          ...(app ? { app_name: app } : {}),
          ...(selectedOs !== 'all' ? { os_type: selectedOs } : {}),
        },
      })
      .then((res) => {
        setData(res.data);
        setErrorMessage('');
      })
      .catch(() => {
        setData(null);
        setErrorMessage('데이터 로드에 실패했습니다.');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, appName, selectedOs]);

  useEffect(() => {
    fetchAppOptions();
  }, [fetchAppOptions]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 상품 종류(설명)가 소수이므로 날짜 × 상품 피벗 테이블로 시각화
  type ProductCell = { txnCount: number; totalUsedPoint: number; totalExchangeAmount: number };
  const products = useMemo(() => {
    const seen = new Map<string, number>(); // description -> usePoint (정렬용)
    for (const item of data ?? []) {
      if (!seen.has(item.description)) seen.set(item.description, item.usePoint);
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([description]) => description);
  }, [data]);

  const pivotRows = useMemo(() => {
    const byDate = new Map<string, Map<string, ProductCell>>();
    for (const item of data ?? []) {
      let dateMap = byDate.get(item.date);
      if (!dateMap) {
        dateMap = new Map();
        byDate.set(item.date, dateMap);
      }
      dateMap.set(item.description, {
        txnCount: item.txnCount,
        totalUsedPoint: item.totalUsedPoint,
        totalExchangeAmount: item.totalExchangeAmount,
      });
    }
    return Array.from(byDate.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([date, cellsByProduct]) => ({ date, cellsByProduct }));
  }, [data]);

  const summary = useMemo(() => {
    return (data ?? []).reduce(
      (acc, item) => ({
        totalUsedPoint: acc.totalUsedPoint + item.totalUsedPoint,
        totalExchangeAmount: acc.totalExchangeAmount + item.totalExchangeAmount,
        txnCount: acc.txnCount + item.txnCount,
      }),
      { totalUsedPoint: 0, totalExchangeAmount: 0, txnCount: 0 },
    );
  }, [data]);

  return (
    <Box>
      {/* 상단 필터 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1.5, md: 0 },
        }}
      >
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <LotteryWeekSelector
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        </Box>
        <Box
          sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 1.5, md: 0 }, width: { xs: '100%', md: 'auto' } }}
        >
          <FilterSelectorWithApp
            selectedApp={appName}
            appOptions={appOptions}
            onAppChange={(v) => setAppName(v ?? '앱 전체')}
            onSearch={fetchData}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
          />
        </Box>
      </Box>

      {/* 본문 카드 */}
      <Box sx={PAGE_CARD_SX}>
        {/* 앱 필터 Chip */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setAppName(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: appName === name ? 700 : 400,
                backgroundColor: appName === name ? '#0044D2' : '#fff',
                color: appName === name ? '#fff' : '#374151',
                border: `1px solid ${appName === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': { backgroundColor: appName === name ? '#0033a0' : '#f3f4f6' },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400} gap={2}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography>데이터 로딩 중...</Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            {/* 요약 카드 */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
                mb: 4,
              }}
            >
              <StatCard label="총 사용 포인트" value={fmt(summary.totalUsedPoint)} unit="P" />
              <StatCard label="총 교환 포인트" value={fmt(summary.totalExchangeAmount)} unit="P" />
              <StatCard label="총 거래 건수" value={fmt(summary.txnCount)} unit="건" />
            </Box>

            <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

            {/* 상품별 교환 현황 테이블 */}
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>
                상품별 교환 현황
              </Typography>

              {pivotRows.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                  <Typography color="#94a3b8">데이터가 없습니다.</Typography>
                </Box>
              ) : (
                <TableContainer sx={{ border: '1px solid #f1f5f9', borderRadius: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            color: '#475569',
                            borderBottom: '2px solid #e2e8f0',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          날짜
                        </TableCell>
                        {products.map((p) => (
                          <TableCell
                            key={p}
                            align="right"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              color: '#475569',
                              borderBottom: '2px solid #e2e8f0',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {p}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pivotRows.map(({ date, cellsByProduct }) => (
                        <TableRow key={date} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell
                            sx={{
                              fontSize: '0.85rem',
                              color: '#1e293b',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {date}
                          </TableCell>
                          {products.map((p) => {
                            const cell = cellsByProduct.get(p);
                            return (
                              <TableCell key={p} align="right" sx={{ py: 0.9 }}>
                                {cell ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'flex-end',
                                      gap: 0.3,
                                      fontSize: '0.85rem',
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 600, color: '#1e293b', lineHeight: 1.15 }}
                                    >
                                      {fmt(cell.txnCount)}건
                                    </Box>
                                    <Box
                                      component="span"
                                      sx={{ fontSize: '0.75rem', color: '#94a3b8' }}
                                    >
                                      {fmt(cell.totalUsedPoint)}P
                                    </Box>
                                  </Box>
                                ) : (
                                  <Typography sx={{ fontSize: '0.85rem', color: '#cbd5e1' }}>
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
